import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadPdfDocument, renderPageToJpegBlob } from "@/tools/pdf/utils";
import { formatFileSize } from "@/lib/formatFileSize";

/**
 * The original and the result side by side, for any page the person picks, with click-to-enlarge.
 * Where a tool changes how pages LOOK (compress, rotate), this is the check before downloading.
 */

async function renderPage(blob: Blob, pageNumber: number, scale: number): Promise<{ url: string; pages: number }> {
  const pdf = await loadPdfDocument(new File([blob], "preview.pdf", { type: "application/pdf" }));
  const n = Math.min(Math.max(1, pageNumber), pdf.numPages);
  const page = await pdf.getPage(n);
  const img = await renderPageToJpegBlob(page, scale, 0.9);
  return { url: URL.createObjectURL(img), pages: pdf.numPages };
}

function usePageImage(blob: Blob, pageNumber: number, scale: number) {
  const [state, setState] = useState<{ url: string | null; pages: number; failed: boolean }>({ url: null, pages: 0, failed: false });
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setState((s) => ({ ...s, url: null, failed: false }));
    renderPage(blob, pageNumber, scale)
      .then((r) => {
        if (!alive) return URL.revokeObjectURL(r.url);
        made = r.url;
        setState({ url: r.url, pages: r.pages, failed: false });
      })
      .catch(() => alive && setState({ url: null, pages: 0, failed: true }));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [blob, pageNumber, scale]);
  return state;
}

function Side({ title, blob, page, onOpen }: { title: string; blob: Blob; page: number; onOpen: (title: string, blob: Blob) => void }) {
  const { url, failed } = usePageImage(blob, page, 0.9);
  return (
    <div className="space-y-1.5 min-w-0">
      <p className="text-xs font-medium">{title} <span className="text-muted-foreground font-normal">· {formatFileSize(blob.size)}</span></p>
      <button
        type="button"
        onClick={() => onOpen(title, blob)}
        className="block w-full rounded-lg border bg-muted/40 p-2 text-left"
        aria-label={`Enlarge ${title.toLowerCase()} page ${page}`}
        title="Click to enlarge"
      >
        {url ? (
          <img src={url} alt={`${title}, page ${page}`} className="mx-auto max-h-72 w-auto bg-white shadow-sm" />
        ) : failed ? (
          <p className="py-10 text-center text-xs text-muted-foreground">Couldn't draw this page.</p>
        ) : (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
      </button>
    </div>
  );
}

export function PdfBeforeAfter({ original, result, pageCountHint, initialPage = 1, leftTitle = "Original", rightTitle = "Result" }: {
  original: Blob;
  result: Blob;
  pageCountHint?: number;
  /** The page shown first, e.g. the first page a tool actually changed. */
  initialPage?: number;
  leftTitle?: string;
  rightTitle?: string;
}) {
  const [page, setPage] = useState(initialPage);
  const [enlarged, setEnlarged] = useState<{ title: string; blob: Blob } | null>(null);
  const { pages } = usePageImage(result, page, 0.2);
  const total = pageCountHint ?? pages;
  return (
    <div className="space-y-3 rounded-xl border bg-card p-3" aria-label="Before and after">
      <div className="flex items-center gap-2">
        <Label htmlFor="ba-page" className="text-xs font-normal">Compare page</Label>
        <Input
          id="ba-page"
          aria-label="Page to compare"
          type="number"
          min={1}
          max={total || undefined}
          value={page}
          onChange={(e) => {
            const n = Math.floor(Number(e.target.value));
            if (n >= 1) setPage(total ? Math.min(n, total) : n);
          }}
          className="h-8 w-20 text-sm"
        />
        {total > 0 && <span className="text-xs text-muted-foreground">of {total}</span>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Side title={leftTitle} blob={original} page={page} onOpen={(title, blob) => setEnlarged({ title, blob })} />
        <Side title={rightTitle} blob={result} page={page} onOpen={(title, blob) => setEnlarged({ title, blob })} />
      </div>
      <Dialog open={!!enlarged} onOpenChange={(o) => !o && setEnlarged(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{enlarged?.title}, page {page}</DialogTitle>
            <DialogDescription>Drawn larger so you can check the detail.</DialogDescription>
          </DialogHeader>
          {enlarged && <Enlarged blob={enlarged.blob} page={page} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Enlarged({ blob, page }: { blob: Blob; page: number }) {
  const { url } = usePageImage(blob, page, 2);
  return url ? <img src={url} alt={`Page ${page}, enlarged`} className="mx-auto h-auto max-w-full bg-white shadow" /> : <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
}

/** A small picture of one page (the first by default), for a file card. */
export function PdfPageThumb({ file, page = 1, className = "w-12" }: { file: Blob; page?: number; className?: string }) {
  const { url } = usePageImage(file, page, 0.3);
  if (!url) return null;
  return (
    <div className={`${className} shrink-0 overflow-hidden rounded border bg-white`}>
      <img src={url} alt={`Page ${page}`} className="h-auto w-full" />
    </div>
  );
}
